/** Browser-side auth state: storage hygiene, per-browser device id, pending MFA challenge, password rules. */

import { Injectable } from '@angular/core';
import { bytesToBase64Url } from './passkey.service';
import { PendingMfaChallenge, PendingPhoneVerification, TermsAcceptanceRequiredResult } from './auth.models';

/**
 * Survive logout: the per-browser device id backs "remember this device", the email backs the returning sign-in screen,
 * and the two dismissed-notice flags keep a banner from coming back after the next sign-out.
 */
export const PRESERVED_LOCAL_STORAGE_KEYS = [
  'DeviceId',
  'LastLoginEmail',
  // Paired with LastLoginEmail: it is what lets Welcome back render the right button on the first paint.
  // Dropping it here would put every sign-out back to a plain "Continue" that changes once the probe lands.
  'LastLoginPasskeyHint',
  'CookieNoticeDismissed',
  'TermsNoticeDismissed',
] as const;

export function clearLocalStoragePreserving(): void {
  const kept = PRESERVED_LOCAL_STORAGE_KEYS.map((key) => [key, localStorage.getItem(key)] as const);
  localStorage.clear();
  for (const [key, value] of kept) {
    if (value !== null) {
      localStorage.setItem(key, value);
    }
  }
}

const DEVICE_ID_KEY = 'DeviceId';
const DEVICE_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;

/** A random per-browser id the API hashes per user for "remember this device". Never derived from the user agent. */
@Injectable({
  providedIn: 'root',
})
export class DeviceIdService {
  getOrCreate(): string {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing && DEVICE_ID_PATTERN.test(existing)) {
      return existing;
    }
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    const id = bytesToBase64Url(bytes.buffer);
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  }
}

const PENDING_MFA_KEY = 'PendingMfaChallenge';

@Injectable({
  providedIn: 'root',
})
export class MfaChallengeStore {
  set(challenge: PendingMfaChallenge): void {
    sessionStorage.setItem(PENDING_MFA_KEY, JSON.stringify(challenge));
  }

  get(): PendingMfaChallenge | null {
    const raw = sessionStorage.getItem(PENDING_MFA_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as PendingMfaChallenge;
    } catch {
      return null;
    }
  }

  clear(): void {
    sessionStorage.removeItem(PENDING_MFA_KEY);
  }
}

const PENDING_TERMS_KEY = 'PendingTermsAcceptance';

/**
 * Carries the terms token from the sign-in page to the acceptance page. Session-scoped on purpose:
 * closing the tab abandons the attempt, exactly as declining does.
 */
@Injectable({
  providedIn: 'root',
})
export class TermsGateStore {
  set(pending: TermsAcceptanceRequiredResult): void {
    sessionStorage.setItem(PENDING_TERMS_KEY, JSON.stringify(pending));
  }

  get(): TermsAcceptanceRequiredResult | null {
    const raw = sessionStorage.getItem(PENDING_TERMS_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as TermsAcceptanceRequiredResult;
    } catch {
      return null;
    }
  }

  clear(): void {
    sessionStorage.removeItem(PENDING_TERMS_KEY);
  }
}

const PENDING_PHONE_VERIFICATION_KEY = 'PendingPhoneVerification';

/** Carries the mobile-verify challenge from the sign-in page to the mobile-verify page. */
@Injectable({
  providedIn: 'root',
})
export class PhoneVerificationStore {
  set(pending: PendingPhoneVerification): void {
    sessionStorage.setItem(PENDING_PHONE_VERIFICATION_KEY, JSON.stringify(pending));
  }

  get(): PendingPhoneVerification | null {
    const raw = sessionStorage.getItem(PENDING_PHONE_VERIFICATION_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as PendingPhoneVerification;
    } catch {
      return null;
    }
  }

  clear(): void {
    sessionStorage.removeItem(PENDING_PHONE_VERIFICATION_KEY);
  }
}

const COOKIE_NOTICE_KEY = 'CookieNoticeDismissed';

/**
 * Remembers, per browser, that the cookie notice on the onboarding screens was dismissed. Display-only: nothing is
 * accepted or sent to the server, and it is kept in localStorage rather than a cookie on purpose.
 */
@Injectable({
  providedIn: 'root',
})
export class CookieNoticeStore {
  isDismissed(): boolean {
    return localStorage.getItem(COOKIE_NOTICE_KEY) === 'true';
  }

  dismiss(): void {
    localStorage.setItem(COOKIE_NOTICE_KEY, 'true');
  }
}

const TERMS_NOTICE_KEY = 'TermsNoticeDismissed';
const LAST_LOGIN_EMAIL_KEY = 'LastLoginEmail';

/**
 * Remembers, per browser, that the "Terms & Conditions Updated" notice on the Terms screen was dismissed. One key
 * holding one notice is enough: a user has exactly one agreement, and the stored value names who dismissed it,
 * which agreement and which version - so a later correction announces itself again, and another user signing in on
 * this browser gets their own. Display-only, like the cookie notice: nothing is accepted and no acceptance record
 * is touched, which is why it is not stored server-side.
 */
@Injectable({
  providedIn: 'root',
})
export class TermsNoticeStore {
  isDismissed(termsType: number, versionLabel: string): boolean {
    return localStorage.getItem(TERMS_NOTICE_KEY) === this.noticeId(termsType, versionLabel);
  }

  dismiss(termsType: number, versionLabel: string): void {
    localStorage.setItem(TERMS_NOTICE_KEY, this.noticeId(termsType, versionLabel));
  }

  /**
   * Keyed on the sign-in email, which every portal already records and already keeps across sign-out. A browser
   * with none recorded matches nothing, so the notice is shown again rather than silently suppressed for whoever
   * signs in next.
   */
  private noticeId(termsType: number, versionLabel: string): string {
    return `${localStorage.getItem(LAST_LOGIN_EMAIL_KEY) ?? ''}|${termsType}|${versionLabel}`;
  }
}

/** Mirrors the API's Identity password options: length ≥ 8, upper + lower case, a digit. */
export interface PasswordRuleState {
  minLength: boolean;
  upperAndLower: boolean;
  digit: boolean;
  all: boolean;
}

export function evaluatePassword(password: string): PasswordRuleState {
  const minLength = password.length >= 8;
  const upperAndLower = /[a-z]/.test(password) && /[A-Z]/.test(password);
  const digit = /\d/.test(password);
  return { minLength, upperAndLower, digit, all: minLength && upperAndLower && digit };
}

/** Logo shown above the standalone auth screens (wizard, passkey setup). */
export const AUTH_LOGO = 'assets/images/login_logo.png';

/**
 * `/setup-passkey?returnUrl=…` from the profile: only an in-app path is honoured, anything else falls back to the
 * normal landing page, so the query string can never send anyone off-site.
 */
export function sanitizeReturnUrl(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) {
    return null;
  }
  return raw;
}
